/** @typedef {import("./types").MfeDefinition} MfeDefinition */

/**
 * Central MFE registry — the ONE file to edit when adding a new MFE.
 *
 * ──────────────────────────────────────────────────────────────────────
 * HOW TO REGISTER A NEW MFE
 * ──────────────────────────────────────────────────────────────────────
 *
 * 1. Add an entry to the `mfeRegistry` array below.
 * 2. The RuntimeLoader in `remoteLoaders.js` will automatically pick up
 *    the new entry via `getMfeByName()` — no manual import map needed.
 * 3. For the legacy host (ui-track), also register in
 *    `ui-track/dev/public/assets/js/mfe/mfeConfig.js` + add the mount div.
 *    (Unchanged workflow — not owned by this repo.)
 *
 * ──────────────────────────────────────────────────────────────────────
 * LOCAL vs REMOTE
 * ──────────────────────────────────────────────────────────────────────
 *
 *   source: "local"   → MFE lives in this monorepo (apps/<name>).
 *                       `pnpm dev` at the repo root starts its MF server
 *                       automatically via Turbo — no extra command needed.
 *
 *   source: "remote"  → MFE lives in a separate repo (e.g. ispm-ui).
 *                       That repo runs its OWN dev server. Only the
 *                       `devUrl` + `scope` need to match what ispm host
 *                       expects.
 *
 * @type {MfeDefinition[]}
 */
export const mfeRegistry = [
  {
    name: "integrations",
    packageName: "@saviynt/integrations",
    routePrefix: "/integrations",
    singleSpa: {
      port: 3001,
      scriptUrl: "/ECMv6/mfe/integrations/integrations-app.js",
    },
    moduleFederation: {
      scope: "integrations",
      exposedModule: "./App",
      devUrl: "http://localhost:3002/remoteEntry.js",
      prodUrl: "/mfe/integrations/module-federation/remoteEntry.js",
    },
    source: "local",
  },

  {
    name: "agent",
    packageName: "@saviynt/agent",
    routePrefix: "/agent",
    singleSpa: {
      port: 3003,
      scriptUrl: "/ECMv6/mfe/agent/agent-app.js",
    },
    moduleFederation: {
      scope: "agent",
      exposedModule: "./App",
      devUrl: "http://localhost:3004/remoteEntry.js",
      prodUrl: "/mfe/agent/module-federation/remoteEntry.js",
    },
    source: "local",
  },

  {
    name: "agent-gateway",
    packageName: "@saviynt/agent-gateway",
    routePrefix: "/agent-gateway",
    singleSpa: {
      port: 3100,
      scriptUrl: "/ECMv6/mfe/agent-gateway/agent-gateway-app.js",
    },
    moduleFederation: {
      scope: "agentGateway",
      exposedModule: "./App",
      devUrl: "http://localhost:3100/remoteEntry.js",
      prodUrl: "/mfe/agent-gateway/module-federation/remoteEntry.js",
    },
    source: "remote",
  },

  {
    name: "policies",
    packageName: "@saviynt/policies",
    routePrefix: "/policies",
    singleSpa: {
      port: 3006,
      scriptUrl: "/ECMv6/mfe/policies/policies-app.js",
    },
    moduleFederation: {
      scope: "policies",
      exposedModule: "./App",
      devUrl: "http://localhost:3007/remoteEntry.js",
      prodUrl: "/mfe/policies/module-federation/remoteEntry.js",
    },
    source: "local",
  },

  {
    name: "nhi-olcm",
    packageName: "@saviynt/nhi-olcm",
    routePrefix: "/nhi-olcm",
    singleSpa: {
      port: 3008,
      scriptUrl: "/ECMv6/mfe/nhi-olcm/nhi-olcm-app.js",
    },
    moduleFederation: {
      scope: "nhi_olcm",
      exposedModule: "./App",
      devUrl: "http://localhost:3009/remoteEntry.js",
      prodUrl: "/mfe/nhi-olcm/module-federation/remoteEntry.js",
    },
    source: "local",
  },

  {
    name: "ispm-ui",
    packageName: "@saviynt/ispm-ui",
    routePrefix: "/dashboard",
    singleSpa: {
      port: 5000,
      scriptUrl: "/mfe/ispm-ui/ispm-ui-app.js",
    },
    moduleFederation: {
      scope: "ispm_ui",
      exposedModule: "./App",
      devUrl: "http://localhost:5001/remoteEntry.js",
      prodUrl: "/mfe/ispm-ui/module-federation/remoteEntry.js",
    },
    source: "remote",
  },

  {
    name: "universal-onboarding",
    packageName: "@saviynt/universal-onboarding-ui",
    routePrefix: "/integrations/universalOnboarding",
    singleSpa: {
      port: 3005,
      scriptUrl:
        "/ECMv6/mfe/universal-onboarding-ui/universal-onboarding-ui-app.js",
    },
    moduleFederation: {
      scope: "universal_onboarding",
      exposedModule: "./App",
      devUrl: "http://localhost:3005/remoteEntry.js",
      prodUrl: "/mfe/universal-onboarding-ui/module-federation/remoteEntry.js",
    },
    source: "remote",
  },
];
